import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/Einwohnermeldeamt/SEOHead';
import Breadcrumb from '../../components/Einwohnermeldeamt/Breadcrumb';
import LocationInfoGrid from '../../components/Einwohnermeldeamt/LocationInfoGrid';
import LocationCTA from '../../components/Einwohnermeldeamt/LocationCTA';
import LocationCard from '../../components/Einwohnermeldeamt/LocationCard';
import Ortsschild from '../../components/Einwohnermeldeamt/Ortsschild';
import LocationFAQ from '../../components/Einwohnermeldeamt/LocationFAQ';
import {
  getLocationBySlug,
  getRelatedLocationsWithFallback,
  getRelatedLocationsSectionTitle,
  generateBreadcrumbs,
  formatNumber,
  getCleanLocationName
} from '../../utils/einwohnermeldeamtHelpers';
import { Einwohnermeldeamt, BreadcrumbItem } from '../../types/einwohnermeldeamt';

const LocationPage: React.FC = () => {
  const { bundesland, location: locationSlug } = useParams<{ bundesland: string; location: string }>();
  const [location, setLocation] = useState<Einwohnermeldeamt | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [relatedLocations, setRelatedLocations] = useState<Einwohnermeldeamt[]>([]);
  const [relatedSectionTitle, setRelatedSectionTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!bundesland || !locationSlug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const [locationData, breadcrumbsData] = await Promise.all([
          getLocationBySlug(bundesland, locationSlug),
          generateBreadcrumbs(bundesland, locationSlug)
        ]);

        if (!locationData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setLocation(locationData);
        setBreadcrumbs(breadcrumbsData);

        // Load related locations
        const { locations, strategy } = await getRelatedLocationsWithFallback(locationData, 6);
        setRelatedLocations(locations);
        setRelatedSectionTitle(getRelatedLocationsSectionTitle(strategy, locationData));
      } catch (error) {
        console.error('Error loading location data:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [bundesland, locationSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-prussian-blue mb-4"></div>
            <p className="text-gray-600">Lade Daten...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !location) {
    return <Navigate to="/einwohnermeldeamt" replace />;
  }

  const cleanName = getCleanLocationName(location.gemeindeName);

  // FAQ data for structured data
  const faqItems = [
    {
      question: `Welche Dienstleistungen bietet das Einwohnermeldeamt ${cleanName}?`,
      answer: `Das Einwohnermeldeamt ${cleanName} ist zuständig für alle melderechtlichen Angelegenheiten. Zu den wichtigsten Dienstleistungen gehören: An- und Abmeldung bei Umzügen, Ausstellung von Meldebescheinigungen, Melderegisterauskünfte, Beantragung und Verlängerung von Personalausweisen und Reisepässen sowie die Ausstellung von Führungszeugnissen.`
    },
    {
      question: `Wie kann ich eine Melderegisterauskunft ${cleanName} beantragen?`,
      answer: `Eine Melderegisterauskunft können Sie entweder persönlich beim Einwohnermeldeamt ${cleanName}, schriftlich per Post oder bequem online über AdressFinder.org beantragen. Die Online-Beantragung ist 24/7 verfügbar und spart Ihnen den Behördengang. Sie benötigen für die Auskunft ein berechtigtes Interesse und müssen die gesuchte Person ausreichend identifizieren können.`
    },
    {
      question: `Wo befindet sich das Einwohnermeldeamt ${cleanName}?`,
      answer: location.strasse && location.ort
        ? `Das Einwohnermeldeamt ${cleanName} befindet sich in der ${location.strasse}, ${location.ort}${location.bundesland ? `, ${location.bundesland}` : ''}.`
        : `Das Einwohnermeldeamt ${cleanName} ist für alle melderechtlichen Angelegenheiten der Bürgerinnen und Bürger zuständig. Die genaue Adresse und Kontaktdaten finden Sie auf dieser Seite oben.`
    },
    {
      question: `Kann ich beim Einwohnermeldeamt ${cleanName} auch online Termine vereinbaren?`,
      answer: `Informationen zur Terminvereinbarung erhalten Sie direkt beim Einwohnermeldeamt ${cleanName}. Viele Ämter bieten mittlerweile Online-Terminbuchungen an. Für Melderegisterauskünfte können Sie AdressFinder.org nutzen – ohne Terminvereinbarung, 24/7 verfügbar.`
    },
    {
      question: `Welche Unterlagen benötige ich für eine An- oder Ummeldung in ${cleanName}?`,
      answer: `Für eine An- oder Ummeldung beim Einwohnermeldeamt ${cleanName} benötigen Sie in der Regel: einen gültigen Personalausweis oder Reisepass, die Wohnungsgeberbestätigung (vom Vermieter ausgefüllt) sowie ggf. weitere Dokumente wie Heiratsurkunde oder Geburtsurkunden der Kinder. Die Anmeldung muss innerhalb von 14 Tagen nach dem Einzug erfolgen.`
    },
    {
      question: `Bietet das Einwohnermeldeamt ${cleanName} auch Online-Services an?`,
      answer: `Viele Meldeämter in Deutschland erweitern zunehmend ihre Online-Angebote. Ob das Einwohnermeldeamt ${cleanName} Online-Services anbietet, erfahren Sie auf der offiziellen Webseite der Gemeinde. Unabhängig davon können Sie Melderegisterauskünfte bequem online über AdressFinder.org beantragen – ohne Behördenbesuch, schnell und unkompliziert.`
    }
  ];

  // Generate Ortsschild image URL for structured data
  const sanitizeFilename = (name: string): string => {
    return name
      .replace(/\//g, '-')
      .replace(/\\/g, '-')
      .replace(/:/g, '-')
      .replace(/\*/g, '-')
      .replace(/\?/g, '-')
      .replace(/"/g, "'")
      .replace(/</g, '-')
      .replace(/>/g, '-')
      .replace(/\|/g, '-')
      .trim();
  };
  const sanitizedName = sanitizeFilename(cleanName);
  const ortsschildImageUrl = `https://www.adressfinder.org/ortsschild/Einwohnermeldeamt-${sanitizedName}.png`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'GovernmentOffice',
        name: `Einwohnermeldeamt ${cleanName}`,
        description: location.highlight,
        image: ortsschildImageUrl,
        address: {
          '@type': 'PostalAddress',
          streetAddress: location.strasse,
          addressLocality: location.ort,
          postalCode: location.postleitzahl,
          addressRegion: location.bundesland,
          addressCountry: 'DE'
        },
        areaServed: {
          '@type': 'City',
          name: cleanName
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.7',
          bestRating: '5',
          worstRating: '1',
          ratingCount: '89'
        }
      },
      {
        '@type': 'ImageObject',
        contentUrl: ortsschildImageUrl,
        name: `Ortsschild ${cleanName}`,
        description: `Ortsschild ${cleanName} - Einwohnermeldeamt`,
        width: '800',
        height: '600',
        encodingFormat: 'image/png',
        about: {
          '@type': 'Place',
          name: cleanName
        }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.label,
          item: `https://www.adressfinder.org${crumb.path}`
        }))
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={`Einwohnermeldeamt ${cleanName} ► Melderegister Online-Service | ${location.bundesland}`}
        description={`📍 Einwohnermeldeamt ${cleanName} ► Kontaktdaten ✓ Öffnungszeiten ✓ Online-Adressauskunft 24/7 verfügbar`}
        canonical={`https://www.adressfinder.org/einwohnermeldeamt/${bundesland}/${locationSlug}`}
        ogType="place"
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-8 md:py-12 px-4 bg-gradient-to-br from-blue-50 to-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <Breadcrumb items={breadcrumbs} />
            
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-1 max-w-4xl w-full">
                {/* Title */}
                <h1 
                  className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
                  style={{ color: '#023047', lineHeight: '1.2' }}
                >
                  Einwohnermeldeamt {cleanName}
                </h1>
              
                {/* Highlight text */}
                {location.highlight && location.highlight.trim() && (
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                    {location.highlight}
                  </p>
                )}

                {/* Quick Info - only show badges with available data */}
                <div className="flex flex-wrap gap-4">
                  {location.postleitzahl && location.postleitzahl.trim() && (
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 font-medium">PLZ {location.postleitzahl}</span>
                    </div>
                  )}
                  
                  {location.einwohner && location.einwohner > 0 && (
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                      <span className="text-gray-700 font-medium">{formatNumber(location.einwohner)} Einwohner</span>
                    </div>
                  )}
                  
                  {location.kreis && location.kreis.trim() && (
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 font-medium">{location.kreis}</span>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <div className="mt-8">
                  <Link
                    to="/personal-data"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg text-white font-semibold text-base shadow-md hover:shadow-xl transition-all transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ backgroundColor: '#023047' }}
                    aria-label="Neue Adressauskunft starten"
                  >
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                      />
                    </svg>
                    Neue Adressauskunft
                    <svg 
                      className="w-5 h-5" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  </Link>
                </div>

                {/* Ortsschild (German place name sign) - Mobile only, below CTA */}
                <div className="mt-6 sm:hidden flex justify-center">
                  <div style={{ maxWidth: '140px', width: '140px' }}>
                    <Ortsschild cityName={location.gemeindeName} className="rounded-lg shadow-md" />
                  </div>
                </div>
              </div>
              
              {/* Ortsschild (German place name sign) - Desktop only */}
              <div className="hidden sm:block flex-shrink-0" style={{ maxWidth: '280px' }}>
                <Ortsschild cityName={location.gemeindeName} className="rounded-lg shadow-lg" />
              </div>
            </div>
          </div>
        </section>

        {/* Information Grid */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ color: '#023047' }}>
              Informationen zum Meldeamt in {cleanName}
            </h2>
            
            <LocationInfoGrid location={location} />
          </div>
        </section>

        {/* Main CTA Section */}
        <section className="py-12 px-4 bg-gray-50">
          <div className="container mx-auto" style={{ maxWidth: '1000px' }}>
            <LocationCTA locationName={cleanName} />
          </div>
        </section>

        {/* About Section */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#023047' }}>
                Über das Einwohnermeldeamt {cleanName}
              </h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Das Einwohnermeldeamt {cleanName} ist zuständig für alle melderechtlichen 
                  Angelegenheiten der Bürgerinnen und Bürger.
                  {location.kreis && location.kreis.trim() && ` Die Gemeinde liegt im Kreis ${location.kreis}`}
                  {location.bundesland && location.bundesland.trim() && ` im Bundesland ${location.bundesland}`}
                  {location.einwohner && location.einwohner > 0 && ` und hat etwa ${formatNumber(location.einwohner)} Einwohner`}.
                </p>
                
                <h3 className="text-xl font-bold mt-6 mb-4" style={{ color: '#023047' }}>
                  Leistungen des Meldeamts
                </h3>
                
                <p>
                  Das Einwohnermeldeamt bietet verschiedene Dienstleistungen an, darunter:
                </p>
                
                <ul className="list-disc pl-6 space-y-2">
                  <li>An- und Abmeldung bei Umzügen</li>
                  <li>Ausstellung von Meldebescheinigungen</li>
                  <li>Beantragung und Verlängerung von Personalausweisen</li>
                  <li>Melderegisterauskünfte</li>
                  <li>Führungszeugnisse</li>
                </ul>
                
                <h3 className="text-xl font-bold mt-6 mb-4" style={{ color: '#023047' }}>
                  Adressauskunft online beantragen
                </h3>
                
                <p>
                  Sie benötigen die aktuelle Meldeanschrift einer Person aus {cleanName}? 
                  Mit AdressFinder.org können Sie eine Melderegisterauskunft bequem online beantragen – 
                  ohne persönlichen Behördengang, 24/7 verfügbar. Starten Sie jetzt 
                  Ihre Anfrage und erhalten Sie die gewünschten Informationen schnell und unkompliziert.
                </p>
              </div>
              
              <div className="mt-8">
                <Link
                  to="/personal-data"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-md text-white shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1"
                  style={{
                    backgroundColor: '#023047',
                    fontWeight: 600,
                    fontSize: '18px'
                  }}
                >
                  Jetzt Adressauskunft starten
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 px-4 bg-gray-50">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ color: '#023047' }}>
                Häufig gestellte Fragen zum Einwohnermeldeamt {cleanName}
              </h2>
              
              <LocationFAQ 
                locationName={cleanName}
                locationData={{
                  bundesland: location.bundesland,
                  kreis: location.kreis,
                  strasse: location.strasse,
                  postleitzahl: location.postleitzahl,
                  ort: location.ort
                }}
              />
            </div>
          </div>
        </section>

        {/* Related Locations */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ color: '#023047' }}>
              {relatedSectionTitle}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedLocations.map(relatedLocation => (
                <LocationCard key={relatedLocation.slug} location={relatedLocation} />
              ))}
            </div>
            
            <div className="mt-8 text-center">
              <Link
                to={`/einwohnermeldeamt/${bundesland}`}
                className="inline-flex items-center gap-2 text-lg font-medium hover:underline"
                style={{ color: '#219ebc' }}
              >
                Alle Meldeämter in {location.bundesland} anzeigen
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default LocationPage;

